import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { DeliveryInfo, Product } from "../types/order";
import { getProductQuantity } from "../utils";

interface DeliveryTableProps {
  deliveries: DeliveryInfo[];
  onEdit: (
    id: string | number,
    statusData: {
      status: string;
      date: string;
      assign_to: string | number;
      quantity: number;
    }
  ) => void;
  products: Product[];
  deliveryButtonState: boolean;
  finalStatus: any;
}

export const DeliveryTable = ({
  deliveries,
  products,
  deliveryButtonState,
  finalStatus,
  onEdit,
}: DeliveryTableProps) => {
  // 1️⃣ Filter out deliveries where the associated product is rejected
  const visibleDeliveries = deliveries.filter((delivery) => {
    // Check if delivery has multiple products
    if (delivery.products && Array.isArray(delivery.products)) {
      // For grouped deliveries, check if all products are rejected
      return delivery.products.some((deliveryProduct: { id: string; name: string; tracking_id: string }) => {
        const product = products.find((p) => p.id === deliveryProduct.id);
        return !product || product.status !== "Rejected";
      });
    }

    // For single product deliveries
    const product = products.find(
      (p) => p.id === delivery.generic_order_product_id
    );
    return !product || product.status !== "Rejected";
  });

  return (
    <div className="">
      <Table className="rounded-t-lg">
        <TableHeader className="bg-[#EEEEEE] rounded-t-lg">
          <TableRow className="whitespace-nowrap rounded-t-lg text-[#666666] font-medium text-sm leading-4 tracking-[2%]">
            <TableHead>Delivery ID</TableHead>
            {/* <TableHead>Customers</TableHead> */}
            <TableHead>Products</TableHead>
            <TableHead>Delivery Mode</TableHead>
            <TableHead>Delivery Address</TableHead>
            <TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {visibleDeliveries.map((delivery) => {
            const address = Object.entries(delivery.address_detail)
              .filter(([key]) => key !== "id" && key !== "created_at")
              .map(([, value]) => value)
              .filter(Boolean)
              .join(", ");

            const defaultData = {
              status: delivery.Status,
              date: delivery.delivered_at || new Date().toISOString().split("T")[0],
              assign_to: delivery?.assign_to ?? "",
              quantity: getProductQuantity(
                delivery.generic_order_product_id.split(",").length > 1
                  ? delivery.generic_order_product_id.split(",")
                  : [delivery.generic_order_product_id],
                products
              ),
            };
            console.log('defaultData', defaultData);
            const productName = delivery?.product_name === 'Encashment' ? 'Forex Card' : delivery?.product_name;

            const isDisabled =
              deliveryButtonState ||
              delivery.Status === "Delivered" ||
              delivery.Status === "Cancelled" ||
              finalStatus?.length > 0;

            return (
              <TableRow
                className="py-2 px-4"
                key={delivery.uuid ?? Math.random().toString()}
              >
                <TableCell>{delivery.delivery_id || "-"}</TableCell>
                {/* <TableCell>{delivery.full_name}</TableCell> */}

                <TableCell>
                  <div className="flex flex-col gap-1">
                    {delivery.products && delivery.products.length > 0 ? (
                      delivery.products.map((product, idx) => (
                        <div key={product.id} className="flex flex-col">
                          <span>{product.name || "-"}</span>
                          {product.tracking_id &&
                            delivery.delivery_mode === "courier" && (
                              <span className="text-sm font-normal text-[#666666]">
                                {product.tracking_id}
                              </span>
                            )}
                          {idx < delivery.products!.length - 1 && (
                            <hr className="my-1 border-gray-200" />
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="flex flex-col">
                        <span>{productName || "-"}</span>
                        {delivery?.tracking_id &&
                          delivery.delivery_mode === "courier" && (
                            <span className="text-sm font-normal text-[#666666] mt-1">
                              {delivery.tracking_id}
                            </span>
                          )}
                      </div>
                    )}
                  </div>
                </TableCell>

                <TableCell>
                  <span className="capitalize">{delivery.delivery_mode || "-"}</span>
                  <br />
                  <span className="text-gray-500 text-sm">
                    {delivery?._staff?.fname ?? ""}
                  </span>
                </TableCell>

                <TableCell className="max-w-[200px]">{address}</TableCell>

                <TableCell className="flex items-center flex-col gap-2">
                  {delivery.Status}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      disabled={isDisabled}
                      onClick={() => onEdit(delivery.uuid, defaultData)}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3.34375 4.375H2.51042C2.06839 4.375 1.64447 4.55059 1.33191 4.86316C1.01934 5.17572 0.84375 5.59964 0.84375 6.04167V13.5417C0.84375 13.9837 1.01934 14.4076 1.33191 14.7202C1.64447 15.0327 2.06839 15.2083 2.51042 15.2083H10.0104C10.4524 15.2083 10.8764 15.0327 11.1889 14.7202C11.5015 14.4076 11.6771 13.9837 11.6771 13.5417V12.7083"
                          stroke="#165FE3"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M10.8411 2.71363L13.3411 5.21363M14.4953 4.03446C14.8235 3.70626 15.0079 3.26112 15.0079 2.79696C15.0079 2.33281 14.8235 1.88767 14.4953 1.55946C14.1671 1.23126 13.722 1.04688 13.2578 1.04688C12.7937 1.04687 12.3485 1.23126 12.0203 1.55946L5.00781 8.54696V11.047H7.50781L14.4953 4.03446Z"
                          stroke="#165FE3"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};